import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { SITE } from "../data/site";
import Socials from "./Socials";
import useReveal from "../hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = ["Theatre", "Film", "Television", "Commercial", "Voice Over", "Representation", "Other"];

export default function Contact() {
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", email: "", subject: "", project_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || "Booking / Inquiry",
        project_type: form.project_type || null,
        message: form.message.trim(),
      });
      if (data?.ok) {
        toast.success("Message sent. Isaiah will be in touch.");
        setForm({ name: "", email: "", subject: "", project_type: "", message: "" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail || "Could not send your message. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Could not send your message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 sm:py-36 lg:py-44 grain"
    >
      <div ref={ref} className="reveal max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left column — info */}
          <div className="lg:col-span-5">
            <div className="overline text-[#991B1B] mb-6">— Contact / Booking</div>
            <h2
              data-testid="contact-title"
              className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[0.95]"
            >
              Let's <span className="italic text-[#A0A0A8]">work.</span>
            </h2>
            <p className="mt-8 text-[#A0A0A8] text-base sm:text-lg leading-relaxed max-w-md">
              For bookings, auditions, representation inquiries, and collaborations — reach out directly or send a message via the form.
            </p>

            <div className="mt-12 space-y-6">
              <a
                data-testid="contact-email-link"
                href={SITE.contact.emailHref}
                className="flex items-start gap-4 group"
              >
                <div className="h-10 w-10 border border-white/10 flex items-center justify-center group-hover:border-[#991B1B] transition-colors">
                  <Mail size={16} className="text-[#D4AF37]" />
                </div>
                <div>
                  <div className="overline mb-1">Email</div>
                  <div className="text-white cine-link">{SITE.contact.email}</div>
                </div>
              </a>

              <a
                data-testid="contact-phone-link"
                href={SITE.contact.phoneHref}
                className="flex items-start gap-4 group"
              >
                <div className="h-10 w-10 border border-white/10 flex items-center justify-center group-hover:border-[#991B1B] transition-colors">
                  <Phone size={16} className="text-[#D4AF37]" />
                </div>
                <div>
                  <div className="overline mb-1">Phone</div>
                  <div className="text-white cine-link">{SITE.contact.phone}</div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="h-10 w-10 border border-white/10 flex items-center justify-center">
                  <MapPin size={16} className="text-[#D4AF37]" />
                </div>
                <div>
                  <div className="overline mb-1">Based</div>
                  <div className="text-white">{SITE.contact.location}</div>
                </div>
              </div>
            </div>

            {/* Socials */}
            <div className="mt-10">
              <div className="overline mb-4">Follow</div>
              <Socials variant="row" />
            </div>

            {/* Representation block */}
            <div
              data-testid="representation-block"
              className="mt-14 border border-[#991B1B]/40 bg-gradient-to-br from-[#991B1B]/10 to-transparent p-8"
            >
              <div className="overline text-[#991B1B] mb-3">Representation</div>
              <div className="font-serif-display italic text-2xl text-white leading-tight">
                {SITE.representation.status}
              </div>
              <p className="mt-4 text-[#A0A0A8] text-sm leading-relaxed">
                {SITE.representation.note}
              </p>
            </div>
          </div>

          {/* Right column — form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border border-white/10 bg-[#121215] p-8 sm:p-12"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="overline block mb-3">Your Name</label>
                  <input
                    data-testid="contact-input-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Casting"
                    className="cine-input"
                  />
                </div>
                <div>
                  <label className="overline block mb-3">Email</label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@studio.com"
                    className="cine-input"
                  />
                </div>
                <div>
                  <label className="overline block mb-3">Subject</label>
                  <input
                    data-testid="contact-input-subject"
                    type="text"
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="Audition · Booking · Inquiry"
                    className="cine-input"
                  />
                </div>
                <div>
                  <label className="overline block mb-3">Project Type</label>
                  <select
                    data-testid="contact-input-project-type"
                    value={form.project_type}
                    onChange={update("project_type")}
                    className="cine-input bg-[#121215]"
                  >
                    <option value="">Select…</option>
                    {PROJECT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-3">Message</label>
                  <textarea
                    data-testid="contact-input-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell Isaiah about the role, project, dates, or what you're looking for."
                    className="cine-input"
                  />
                </div>
              </div>

              <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
                <p className="text-xs text-[#6B6B72] max-w-md leading-relaxed">
                  By submitting, you consent to be contacted regarding your inquiry. Your details are never shared.
                </p>
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="btn-cine disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Send Message"}
                  <Send size={14} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
